import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowPathIcon, BeakerIcon, CheckCircleIcon, ClockIcon,
  ExclamationTriangleIcon, LanguageIcon, MicrophoneIcon,
  ScaleIcon, SparklesIcon, UserGroupIcon,
} from '@heroicons/react/24/outline';
import { apiService } from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';

type Counts = Record<string, number>;
interface PilotSummary {
  since: string;
  counts: Counts;
  durations: Array<{ workflow: string; median_ms: number | string; p95_ms: number | string }>;
  variants: Array<{ variant: string; sessions: number | string }>;
  rates: Record<string, number | null>;
}
interface AiMetrics {
  since: string;
  summary: { requests: number; successes: number; failures: number; median_latency_ms: number | null; p95_latency_ms: number | null };
  byFeature: Array<{ feature: string; requests: number | string; failures: number | string; average_latency_ms: number | string }>;
  feedback: Array<{ decision: string; count: number | string }>;
  overrideRate: number | null;
  confidentWrong: { threshold: number; count: number; ofConfidentTotal: number; rate: number | null };
}

const pct = (value: number | null | undefined) => value == null ? '—' : `${Math.round(value * 100)}%`;
const seconds = (value: number | string | null | undefined) => value == null ? '—' : `${(Number(value) / 1000).toFixed(1)}s`;
const label = (value: string) => value.replace(/_/g, ' ').replace(/\b\w/g, (x) => x.toUpperCase());

const RateBar: React.FC<{ name: string; value: number | null; note: string }> = ({ name, value, note }) => (
  <div className="border-b border-gray-200 py-4 last:border-0 dark:border-gray-700">
    <div className="flex items-start justify-between gap-4">
      <div><p className="text-sm font-medium text-primary-900 dark:text-gray-100">{name}</p><p className="mt-0.5 text-xs text-gray-500">{note}</p></div>
      <span className="text-sm font-semibold text-primary-800 dark:text-primary-200">{pct(value)}</span>
    </div>
    <div className="mt-2 h-2 overflow-hidden rounded-sm bg-gray-200 dark:bg-gray-700">
      <div className="h-full bg-primary-600" style={{ width: `${Math.max(0, Math.min(100, (value || 0) * 100))}%` }} />
    </div>
  </div>
);

const PilotEvidenceDashboard: React.FC = () => {
  const [days, setDays] = useState(30);
  const [pilot, setPilot] = useState<PilotSummary | null>(null);
  const [ai, setAi] = useState<AiMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true); setError('');
    try {
      const [p, a] = await Promise.all([
        apiService.get<any>(`/pilot/summary?days=${days}`),
        apiService.get<any>(`/ai-operations/metrics?days=${days}`),
      ]);
      setPilot(p.data); setAi(a.data);
    } catch (e) {
      setError('Pilot evidence could not be loaded. Confirm the latest migrations are applied and your role can read reports.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);

  const sessions = useMemo(() => (pilot?.variants || []).reduce((n, x) => n + Number(x.sessions || 0), 0), [pilot]);
  const submitted = pilot?.counts?.report_submitted || 0;
  const duration = pilot?.durations?.find((d) => d.workflow === 'citizen_report');
  const aiFailureRate = ai?.summary?.requests ? ai.summary.failures / ai.summary.requests : null;
  const translationReviews = (pilot?.counts?.translation_accepted || 0) + (pilot?.counts?.translation_modified || 0) + (pilot?.counts?.translation_rejected || 0);

  return (
    <div className="space-y-6">
      <PageHeader title="Pilot Evidence" subtitle="Controlled pilot outcomes and Gemini operational reliability—without storing resident content in analytics." actions={
        <div className="flex items-center gap-2">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-sm border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800">
            <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={60}>Last 60 days</option><option value={90}>Last 90 days</option>
          </select>
          <Button variant="secondary" onClick={load} disabled={loading}><ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
        </div>
      } />

      <div className="rounded-sm border border-accent-300 bg-accent-50 p-4 text-sm text-accent-900 dark:border-accent-700 dark:bg-accent-900/20 dark:text-accent-200">
        <strong>Evidence status:</strong> Results are pilot observations. Always disclose dates, sample size, assignment method and missing data; do not present them as municipality-wide impact proof.
      </div>
      {error && <div className="rounded-sm border border-red-300 bg-red-50 p-4 text-sm text-red-800">{error}</div>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Pilot sessions" value={loading ? '…' : sessions} icon={UserGroupIcon} tone="blue" hint="Distinct baseline and assisted journeys" />
        <StatCard label="Reports submitted" value={loading ? '…' : submitted} icon={CheckCircleIcon} tone="emerald" hint={`${pct(pilot?.rates?.submissionCompletion)} journey completion`} />
        <StatCard label="Median submission" value={loading ? '…' : seconds(duration?.median_ms)} icon={ClockIcon} tone="purple" hint={`P95 ${seconds(duration?.p95_ms)}`} />
        <StatCard label="Gemini fallback rate" value={loading ? '…' : pct(aiFailureRate)} icon={ExclamationTriangleIcon} tone={aiFailureRate && aiFailureRate > .05 ? 'red' : 'amber'} hint={`${ai?.summary?.requests || 0} measured calls`} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <section className="rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between"><h2 className="text-base font-semibold text-primary-900 dark:text-gray-100">Assisted workflow outcomes</h2><Badge tone="info">Human decisions retained</Badge></div>
          <RateBar name="Category suggestion accepted" value={pilot?.rates?.triageAcceptance ?? null} note={`${pilot?.counts?.triage_shown || 0} suggestions shown`} />
          <RateBar name="Voice draft accepted" value={pilot?.rates?.voiceAcceptance ?? null} note={`${pilot?.counts?.voice_draft_shown || 0} structured drafts shown`} />
          <RateBar name="Duplicate warning followed" value={pilot?.rates?.duplicateFollowRate ?? null} note={`${pilot?.counts?.duplicate_shown || 0} warnings shown`} />
          <RateBar name="Translation usable" value={pilot?.rates?.translationUsableRate ?? null} note={`${translationReviews} staff-reviewed drafts`} />
          <RateBar name="AI suggestion override rate" value={ai?.overrideRate ?? null} note="Share of reviewed AI suggestions a human changed or rejected, across all AI-assisted features" />
          <RateBar
            name="Confidently wrong"
            value={ai?.confidentWrong?.rate ?? null}
            note={ai?.confidentWrong?.ofConfidentTotal
              ? `${ai.confidentWrong.count} of ${ai.confidentWrong.ofConfidentTotal} reviewed suggestions above ${pct(ai.confidentWrong.threshold)} confidence were overridden anyway`
              : 'No confidence-scored suggestions have been reviewed yet in this period'}
          />
        </section>

        <section className="rounded-sm border border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="text-base font-semibold text-primary-900 dark:text-gray-100">Baseline versus assisted</h2>
          <p className="mt-1 text-xs text-gray-500">Session counts identify whether the comparison has enough observations; they do not imply causal impact by themselves.</p>
          <div className="mt-4 space-y-3">
            {(pilot?.variants || []).length ? pilot!.variants.map((v) => (
              <div key={v.variant} className="flex items-center justify-between rounded-sm border border-gray-200 px-4 py-3 dark:border-gray-700">
                <div className="flex items-center gap-3">{v.variant === 'baseline' ? <ScaleIcon className="h-5 w-5 text-gray-500" /> : <SparklesIcon className="h-5 w-5 text-primary-600" />}<span className="text-sm font-medium">{label(v.variant)}</span></div>
                <span className="text-lg font-semibold">{Number(v.sessions)}</span>
              </div>
            )) : <p className="py-8 text-center text-sm text-gray-500">No pilot sessions recorded in this period.</p>}
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-sm bg-gray-50 p-3 dark:bg-gray-900">
              <span className="text-gray-500">Category corrections</span>
              <strong className="mt-1 block text-lg">{pct(pilot?.rates?.categoryCorrectionRate)}</strong>
              {pilot?.rates?.categoryCorrectionRate == null && <p className="mt-0.5 text-xs text-gray-400">Not yet instrumented</p>}
            </div>
            <div className="rounded-sm bg-gray-50 p-3 dark:bg-gray-900">
              <span className="text-gray-500">Follow-up contacts</span>
              <strong className="mt-1 block text-lg">{pct(pilot?.rates?.followupContactRate)}</strong>
              {pilot?.rates?.followupContactRate == null && <p className="mt-0.5 text-xs text-gray-400">Not yet instrumented</p>}
            </div>
          </div>
        </section>
      </div>

      <section className="rounded-sm border border-gray-300 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700"><div><h2 className="text-base font-semibold text-primary-900 dark:text-gray-100">Gemini operations by feature</h2><p className="text-xs text-gray-500">Reliability and latency, not resident content</p></div><BeakerIcon className="h-5 w-5 text-primary-600" /></div>
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700"><thead className="bg-gray-50 dark:bg-gray-900"><tr>{['Feature','Requests','Failures','Average latency'].map(h=><th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">{h}</th>)}</tr></thead><tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {(ai?.byFeature || []).map((f)=><tr key={f.feature}><td className="px-5 py-3 font-medium text-primary-900 dark:text-gray-100">{label(f.feature)}</td><td className="px-5 py-3">{Number(f.requests)}</td><td className="px-5 py-3"><Badge tone={Number(f.failures)>0?'danger':'success'}>{Number(f.failures)}</Badge></td><td className="px-5 py-3">{seconds(f.average_latency_ms)}</td></tr>)}
          {!loading && !(ai?.byFeature || []).length && <tr><td colSpan={4} className="px-5 py-8 text-center text-gray-500">No Gemini calls recorded in this period.</td></tr>}
        </tbody></table></div>
      </section>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex gap-3 rounded-sm border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"><LanguageIcon className="h-5 w-5 text-primary-600"/><div><p className="text-sm font-medium">Translation reviews</p><p className="text-xs text-gray-500">{translationReviews} accepted, modified or rejected</p></div></div>
        <div className="flex gap-3 rounded-sm border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"><MicrophoneIcon className="h-5 w-5 text-primary-600"/><div><p className="text-sm font-medium">Voice observations</p><p className="text-xs text-gray-500">{pilot?.counts?.voice_draft_shown || 0} drafts shown</p></div></div>
        <div className="flex gap-3 rounded-sm border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"><SparklesIcon className="h-5 w-5 text-primary-600"/><div><p className="text-sm font-medium">AI review decisions</p><p className="text-xs text-gray-500">{(ai?.feedback || []).reduce((n,x)=>n+Number(x.count),0)} recorded decisions</p></div></div>
      </div>
    </div>
  );
};

export default PilotEvidenceDashboard;
